import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import timelineService from '../services/timelineService';

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;
  return (
    <div className="flex justify-center items-center gap-2 mt-12">
      <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} className="px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">Önceki</button>
      <span className="text-sm text-gray-700">Sayfa {currentPage} / {totalPages}</span>
      <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} className="px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">Sonraki</button>
    </div>
  );
};

const TimelinesPage = () => {
  const [timelines, setTimelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortOption, setSortOption] = useState('trending');
  const [currentPage, setCurrentPage] = useState(1);
  const timelinesPerPage = 18;

  useEffect(() => {
    const fetchTimelines = async () => {
      setLoading(true);
      try {
        const data = await timelineService.getPublicTimelines(sortOption);
        setTimelines(data);
        setCurrentPage(1); // Reset to first page on sort change
      } catch (err) {
        setError('Zaman çizelgeleri yüklenirken bir hata oluştu.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchTimelines();
  }, [sortOption]);

  // Pagination logic
  const totalPages = Math.ceil(timelines.length / timelinesPerPage);
  const paginatedTimelines = timelines.slice((currentPage - 1) * timelinesPerPage, currentPage * timelinesPerPage);

  const SortButton = ({ type, children }) => (
    <button
      onClick={() => setSortOption(type)}
      className={`px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${sortOption === type
          ? 'bg-blue-600 text-white shadow-sm'
          : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
        }`}
    >
      {children}
    </button>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-50 to-gray-100 flex items-center justify-center">
        <p className="text-lg text-gray-700">Yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-50 to-gray-100 flex items-center justify-center">
        <p className="text-lg text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-50 to-gray-100">
      <Navigation />
      <main className="container mx-auto px-4 py-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Zaman Çizelgeleri</h1>
            <div className="flex items-center gap-2">
              <SortButton type="trending">Eğilimler</SortButton>
              <SortButton type="popular">Beğenilen</SortButton>
              <SortButton type="newest">En Yeni</SortButton>
            </div>
          </div>

          {paginatedTimelines.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginatedTimelines.map((timeline) => {
                const imageUrl = timeline.coverImage || timeline.logoUrl || timeline.backgroundImage;
                const bgColor = timeline.backgroundColor || '#FFFFFF';

                return (
                  <Link
                    key={timeline._id}
                    to={`/cizelge/${timelineService.getTimelineUrlIdentifier(timeline)}`}
                    className="group"
                  >
                    <div className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition-shadow duration-300 h-full flex flex-col">
                      <div className="relative h-48">
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={timeline.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div
                            className="w-full h-full"
                            style={{ backgroundColor: bgColor }}
                          />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      </div>

                      <div className="p-6 flex-grow">
                        <h2 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors duration-200">
                          {timeline.title}
                        </h2>
                        <div className="text-gray-600 mb-4 line-clamp-2">
                          {timeline.intro}
                        </div>

                        <div className="flex items-center justify-between mt-auto">
                          <div className="flex items-center text-gray-500 text-sm">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                            </svg>
                            {timeline.startDate?.year} - {timeline.endDate?.year}
                          </div>
                          <div className="flex items-center text-gray-500 text-sm">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                              <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                              <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                            </svg>
                            {timeline.views.toLocaleString('tr-TR')} görüntülenme
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-gray-600">Görüntülenecek zaman çizelgesi bulunamadı.</p>
            </div>
          )}
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </div>
      </main>
    </div>
  );
};

export default TimelinesPage; 